import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { GitCommitIcon, GitPullRequestIcon, ZapIcon } from 'lucide-react';
const agents = [{
  name: 'Cursor',
  type: 'IDE Agent',
  color: '#A855F7',
  bgColor: 'rgba(168, 85, 247, 0.08)',
  borderColor: 'rgba(168, 85, 247, 0.25)',
  commits: 47,
  prs: 12,
  activity: [40, 65, 55, 80, 70, 90, 85],
  description: 'Inline code generation & refactoring'
}, {
  name: 'Claude Code',
  type: 'Terminal Agent',
  color: '#F97316',
  bgColor: 'rgba(249, 115, 22, 0.08)',
  borderColor: 'rgba(249, 115, 22, 0.25)',
  commits: 31,
  prs: 8,
  activity: [20, 45, 60, 40, 75, 65, 80],
  description: 'Full-context terminal automation'
}, {
  name: 'Devin',
  type: 'Autonomous Agent',
  color: '#3B82F6',
  bgColor: 'rgba(59, 130, 246, 0.08)',
  borderColor: 'rgba(59, 130, 246, 0.25)',
  commits: 18,
  prs: 5,
  activity: [10, 30, 25, 50, 45, 60, 55],
  description: 'End-to-end autonomous development'
}, {
  name: 'GitHub Copilot',
  type: 'Assisted Agent',
  color: '#10B981',
  bgColor: 'rgba(16, 185, 129, 0.08)',
  borderColor: 'rgba(16, 185, 129, 0.25)',
  commits: 89,
  prs: 24,
  activity: [60, 75, 80, 85, 90, 88, 95],
  description: 'Suggestion-based code completion'
}];
function ActivityBar({
  values,
  color
}: {
  values: number[];
  color: string;
}) {
  return <div className="flex items-end gap-0.5 h-8" data-id="element-1">
      {values.map((v, i) => <motion.div key={i} className="flex-1 rounded-sm" style={{
      backgroundColor: color,
      opacity: 0.7
    }} initial={{
      height: 0
    }} animate={{
      height: `${v}%`
    }} transition={{
      delay: i * 0.05,
      duration: 0.4,
      ease: 'easeOut'
    }} data-id="element-2" />)}
    </div>;
}
export function AgentDetectionSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="py-24 px-6 bg-[#080C14] relative" data-id="element-3">
      <div className="max-w-6xl mx-auto" data-id="element-4">
        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="mb-16" data-id="element-5">
          <span className="text-xs font-bold tracking-[0.25em] text-cyan-400 uppercase" data-id="element-6">
            Detect & Identify
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-white mt-3 mb-4 tracking-tight" data-id="element-7">
            Know Every Agent in Your Stack
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl" data-id="element-8">
            Automatically detect and classify AI agents operating across your
            engineering organization. Identify autonomous vs assisted
            contributions with precision.
          </p>
        </motion.div>

        {/* Agent cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12" data-id="element-9">
          {agents.map((agent, i) => <motion.div key={agent.name} initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          delay: i * 0.1,
          duration: 0.5
        }} className="rounded-xl p-5 border" style={{
          backgroundColor: agent.bgColor,
          borderColor: agent.borderColor
        }} data-id="element-10">
              <div className="flex items-start justify-between mb-4" data-id="element-11">
                <div data-id="element-12">
                  <div className="flex items-center gap-2 mb-1" data-id="element-13">
                    <span className="w-2 h-2 rounded-full pulse-dot" style={{
                  backgroundColor: agent.color
                }} data-id="element-14" />
                    <span className="text-white font-bold text-base" data-id="element-15">
                      {agent.name}
                    </span>
                  </div>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{
                backgroundColor: `${agent.color}20`,
                color: agent.color
              }} data-id="element-16">
                    {agent.type}
                  </span>
                </div>
              </div>

              <p className="text-gray-500 text-xs mb-4 leading-relaxed" data-id="element-17">
                {agent.description}
              </p>

              <ActivityBar values={agent.activity} color={agent.color} data-id="element-18" />

              <div className="mt-4 grid grid-cols-2 gap-3" data-id="element-19">
                <div className="flex items-center gap-1.5" data-id="element-20">
                  <GitCommitIcon className="w-3 h-3 text-gray-500" data-id="element-21" />
                  <div data-id="element-22">
                    <div className="text-white font-bold text-sm" data-id="element-23">
                      {agent.commits}
                    </div>
                    <div className="text-gray-600 text-[10px]" data-id="element-24">commits/wk</div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-25">
                  <GitPullRequestIcon className="w-3 h-3 text-gray-500" data-id="element-26" />
                  <div data-id="element-27">
                    <div className="text-white font-bold text-sm" data-id="element-28">
                      {agent.prs}
                    </div>
                    <div className="text-gray-600 text-[10px]" data-id="element-29">PRs opened</div>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Autonomous vs Assisted split */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.5,
        duration: 0.6
      }} className="bg-[#0D1421] border border-[#1E2D45] rounded-2xl p-6" data-id="element-30">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-id="element-31">
            <div data-id="element-32">
              <div className="flex items-center gap-2 mb-1" data-id="element-33">
                <ZapIcon className="w-4 h-4 text-cyan-400" data-id="element-34" />
                <span className="text-white font-semibold" data-id="element-35">
                  Contribution Classification
                </span>
              </div>
              <p className="text-gray-500 text-sm" data-id="element-36">
                This week across all repositories
              </p>
            </div>
            <div className="flex gap-6" data-id="element-37">
              <div className="text-center" data-id="element-38">
                <div className="text-3xl font-black text-blue-400" data-id="element-39">34%</div>
                <div className="text-xs text-gray-500 mt-0.5" data-id="element-40">Autonomous</div>
              </div>
              <div className="text-center" data-id="element-41">
                <div className="text-3xl font-black text-cyan-400" data-id="element-42">66%</div>
                <div className="text-xs text-gray-500 mt-0.5" data-id="element-43">Assisted</div>
              </div>
            </div>
          </div>

          <div className="mt-5" data-id="element-44">
            <div className="flex rounded-full overflow-hidden h-3 bg-[#1E2D45]" data-id="element-45">
              <motion.div className="h-full rounded-l-full" style={{
              backgroundColor: '#3B82F6'
            }} initial={{
              width: 0
            }} animate={isInView ? {
              width: '34%'
            } : {}} transition={{
              delay: 0.7,
              duration: 0.8,
              ease: 'easeOut'
            }} data-id="element-46" />
              <motion.div className="h-full rounded-r-full" style={{
              backgroundColor: '#00D4FF'
            }} initial={{
              width: 0
            }} animate={isInView ? {
              width: '66%'
            } : {}} transition={{
              delay: 0.9,
              duration: 0.8,
              ease: 'easeOut'
            }} data-id="element-47" />
            </div>
            <div className="flex justify-between mt-2" data-id="element-48">
              <span className="text-xs text-gray-600" data-id="element-49">Devin + Claude Code</span>
              <span className="text-xs text-gray-600" data-id="element-50">Cursor + Copilot</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}